import Qty from '@/core/types/Qty';
import RpcService, {RequestOptions} from "@/core/services/Rpc/RpcService";
import RpcResponse  from "@/core/services/Rpc/RpcResponse";
import type Tender  from "@/domains/Tenders/Tender";
import type Store   from "@/domains/Stores/Store";
import type ParticipationDetailProduct from "@/domains/ParticipationDetailProduct/ParticipationDetailProduct";
import type TimeStamp from "@/core/types/TimeStamp";
import type DateRange from "@/core/types/DateRange";
import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort from "@/core/services/Rpc/RpcDataSort";

class TendersDAO {

    static async search(pattern: string): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.search', {
            pattern
        });
    }

    /** Получить список тендеров */
    static async getList(
        filters?: {
            updatedAfter?:                   TimeStamp | null;
            isOrdersCreated?:                boolean;
            isBookmarked?:                   boolean;
            isWonByCurrentPartner?:          boolean;
            isParticipatedByCurrentPartner?: boolean;
            isFinished?:                     boolean;
            isDeleted?:                      boolean;
            isPreorderNeeded?:               boolean;
            pattern?:                        string;
            seller?:                         { id: number };
            buyer?:                          { id: number };
            responsible?:                    { id: number };
            category?:                       { id: number };
            desiredReceiptPeriod?:           DateRange;
            endAtPeriod?:                    DateRange;
            winnersCategory?:                { id: number };
            isAdminMode?: 					 boolean;
            status?:                         string;
            withSuppliersStats?:             boolean;
            availableSuppliersCount?:        number;
            hasRefusedSuppliers?:            boolean;
            hasSuppliersWithoutBid?:         boolean;
        },
        pagination?: RpcDataPagination,
        sort?: RpcDataSort,
        options: RequestOptions = {},
    ): Promise<RpcResponse> {
        let status = filters?.status;

        if (filters?.isBookmarked === true) {
            // избранные тендеры запрашиваем только актуальные
            status = 'isActual';
        }
        else {
            // статус как пришёл в фильтрах
        }

        return await RpcService.makeRequestSilent(
            'tenders.getList',
            {
                ...filters,
                status,
                seller:          filters?.seller ? { id: filters.seller.id } : undefined,
                buyer:           filters?.buyer ? { id: filters.buyer.id } : undefined,
                responsible:     filters?.responsible ? { id: filters.responsible.id } : undefined,
                category:        filters?.category ? { id: filters.category.id } : undefined,
                winnersCategory: filters?.winnersCategory ? { id: filters.winnersCategory.id } : undefined,
            },
            pagination,
            sort,
            false,
            options,
        );
    }

    static async get(id: number | string, updatedAfter: Date | null, timezone: string, isDeleted: boolean = false, options: RequestOptions): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.get', {
            id,
            updatedAfter,
            timezone,
            isDeleted
        }, null, null, updatedAfter === null, options);
    }

    static async update(tender: Tender): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.update', {
            tender
        });
    }

    static async add(tender: Tender): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.add', {
            tender
        });
    }

    static async close(id: number): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.close', {
            id
        });
    }

    static async delete(tenderId: number): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.delete', {
            id: tenderId
        });
    }

    static async getPriceForNewTender(product: Array<{ id: number; qty: Qty }>, buyerStore: Store, date: Date): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.getPriceForNewTender', {
            product,
            buyerStore: { id: buyerStore.id },
            date
        });
    }

    /** Получение всех участников для этого тендера: кто сделал ставку, и кто не очень **/
    static async getParticipation(tenderId: number): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.getParticipation', {
            tender: { id: tenderId }
        });
    }

    static async updateParticipation(participation: ParticipationDetailProduct): Promise<RpcResponse> {
        return await RpcService.makeRequestSilent('tenders.updateParticipation', {
            participation
        });
    }

    /** Завершённые тендеры текущего покупателя по номенклатуре для графика истории цен — облегчённые строки */
    static async getPriceHistory(productId: number): Promise<RpcResponse> {
        return await RpcService.makeRequest('tenders.getPriceHistory', { productId });
    }
}

export default TendersDAO;
